/**
 * Runtime configuration loader.
 *
 * The deployed frontend bundle is environment-agnostic — it fetches API
 * endpoints from `/config.json` on startup, which the CDK stack writes to
 * the S3 bucket alongside the frontend assets. For `npm run dev`, the
 * Vite build-time env vars (VITE_HTTP_API_URL, VITE_WS_URL) act as a
 * fallback so local dev still works without a /config.json round-trip.
 */

export interface RuntimeConfig {
  httpApiUrl: string;
  wsUrl: string;
  appVersion?: string;
}

let cached: RuntimeConfig | null = null;
let loadPromise: Promise<RuntimeConfig> | null = null;

const BUNDLE_VERSION =
  typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "dev";
const RELOAD_GUARD_KEY = "binogi-buzz:version-reload-at";
const RELOAD_GUARD_MS = 60_000; // never reload more than once a minute

// If /config.json reports a different appVersion than the one stamped
// into this bundle, the tab is running stale code from before a deploy.
// Force-reload so the browser fetches the new HTML + JS chunks. A
// sessionStorage guard prevents an infinite loop if /config.json is
// itself stale or broken.
function reloadIfStale(serverVersion: string | undefined): boolean {
  if (!serverVersion || serverVersion === BUNDLE_VERSION) return false;
  if (BUNDLE_VERSION === "dev") return false;
  try {
    const last = sessionStorage.getItem(RELOAD_GUARD_KEY);
    if (last && Date.now() - Number(last) < RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable — fall through and reload anyway
  }
  console.warn(
    `[binogi-buzz] bundle version ${BUNDLE_VERSION} != server version ${serverVersion}; reloading`,
  );
  // Cache-busting query param ensures the browser revalidates the HTML
  // shell rather than serving its in-memory cached copy. Put it on this
  // history entry and reload that entry, rather than location.replace():
  // a replace starts a new entry without history.state, which is where a
  // host screen keeps the session it is running.
  const url = new URL(window.location.href);
  url.searchParams.set("_v", serverVersion);
  window.history.replaceState(window.history.state, "", url.toString());
  window.location.reload();
  return true;
}

export async function loadConfig(): Promise<RuntimeConfig> {
  if (cached) return cached;
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    let fromServer: Partial<RuntimeConfig> = {};
    try {
      const res = await fetch("/config.json", { cache: "no-store" });
      if (res.ok) {
        const data = (await res.json()) as Partial<RuntimeConfig>;
        fromServer = data;
      }
    } catch {
      // ignore — fall back to build-time env vars
    }
    // Reload BEFORE returning the config so the rest of the app never
    // mounts against a stale bundle. The returned Promise here is moot —
    // window.location.reload() tears the page down.
    if (reloadIfStale(fromServer.appVersion)) {
      return new Promise<RuntimeConfig>(() => undefined);
    }
    cached = {
      httpApiUrl:
        fromServer.httpApiUrl ?? import.meta.env.VITE_HTTP_API_URL ?? "",
      wsUrl: fromServer.wsUrl ?? import.meta.env.VITE_WS_URL ?? "",
      appVersion: fromServer.appVersion ?? BUNDLE_VERSION,
    };
    return cached;
  })();
  return loadPromise;
}

export function getConfig(): RuntimeConfig {
  if (!cached) {
    throw new Error(
      "Runtime config not loaded yet. Call loadConfig() before reading.",
    );
  }
  return cached;
}
